import { NativeMessages } from 'src/native-messages';
import { appConfigFactory } from '../factories/appConfig.factory';
import { windowFactory } from '../factories/window.factory';
import { EventsManagerImpl } from './events-manager.utility';
import { sanitizeUrl } from './sanitizeUrl';

export interface NativeMessage {
  url: string;
  eventType: string;
  dataString?: any;
}

export enum NavigateType {
  push = 'PUSH',
  pop = 'POP',
  present = 'PRESENT',
  dismiss = 'DISMISS',
}
export interface NavigateMessage {
  type: NavigateType;
  id: string;
  path?: string;
  nativeEvent?: any;
}
export interface NativeBridge {
  toNative(eventType: string, data?: any): void;
  nativeNavigate(
    eventId: string,
    additionalParams?: { type?: string; data?: Record<string, any> },
  ): void;
  openUrl(url: string): void;
  signIn(): void;
  navigate(payload: NavigateMessage): void;
  goToRoot(): void;
  toHybrid(eventType: string, payload?: any): void;
}

export class NativeBridgeImpl implements NativeBridge {
  private static instance: NativeBridge;

  public static getInstance(): NativeBridge {
    if (!NativeBridgeImpl.instance) {
      NativeBridgeImpl.instance = new NativeBridgeImpl();
    }

    return NativeBridgeImpl.instance;
  }

  private eventsManager = EventsManagerImpl.getInstance();
  private networkQueue: NativeMessage[] = [];
  private iframeQueue: string[] = []; // Maintains list of ids generated
  private windowInstance = windowFactory();
  private appConfig: AppConfig = appConfigFactory();

  constructor() {
    this.windowInstance.toHybrid = this.toHybrid;
    setInterval(() => this.pollQueue(), 100);
  }

  public toNative(eventType: string, data?: any): void {
    const eventTypeNormalized = eventType.toLowerCase();
    const dataString = data ? encodeURI(JSON.stringify(data)) : '';
    const url = `${this.appConfig.scheme}://${eventTypeNormalized}#${dataString}`;

    this.networkQueue.push({
      url,
      eventType,
      dataString,
    });
  }

  public nativeNavigate(
    eventId: string,
    additionalParams?: { type?: string; data?: Record<string, any> },
  ): void {
    const { type, data } = additionalParams || { type: null, data: null };
    const payload: {
      id: string;
      type?: string;
      data?: Record<string, any>;
    } = {
      id: eventId,
    };

    if (type) {
      payload.type = type;
    }

    if (data) {
      payload.data = data;
    }

    this.toNative(NativeMessages.NATIVE_NAVIGATE, payload);
  }

  /* ruslanvolyar- this method seems to only be supported on iOS as of 6/17/22
   *  and is not supported on Android.  It opens a url in the native webview.
   */
  public openUrl(url: string): void {
    this.toNative(NativeMessages.OPEN_URL, { url });
  }

  public goToRoot(): void {
    this.toNative(NativeMessages.GO_TO_ROOT);
  }

  public signIn(): void {
    this.toNative(NativeMessages.NEEDS_CREDENTIALS);
  }

  public navigate(payload: NavigateMessage): void {
    this.toNative(NativeMessages.NAVIGATE, payload);
  }

  public toHybrid = (eventType: string, payload?: any): void => {
    this.eventsManager.publish(eventType, payload);
  };

  private pollQueue(): void {
    if (this.shouldFlushIframeQueue()) {
      this.flushIframeQueue();
    }

    this.flushNetworkQueue();
  }

  private shouldFlushIframeQueue(): boolean {
    return (
      this.iframeQueue.length > 0 && this.appConfig.environment === 'production'
    );
  }

  private flushIframeQueue(): void {
    for (const id of this.iframeQueue) {
      document.getElementById(id).remove();
    }
    this.iframeQueue = [];
  }

  private flushNetworkQueue(): void {
    for (const nativeMessage of this.networkQueue) {
      try {
        this.buildIFrame(nativeMessage.url);
        console.log(
          `-- toNative -- eventType: ${
            nativeMessage.eventType
          }, data: ${decodeURI(nativeMessage.dataString)}`,
        );
      } catch (e) {
        console.error(e);
      }
    }

    // purge queue
    this.networkQueue = [];
  }

  private buildIFrame(url: string): void {
    const iframe = document.createElement('iframe');
    iframe.id = Math.random().toString(36).substr(2, 10);
    iframe.style.display = 'none';
    iframe.src = sanitizeUrl(url);
    document.body.appendChild(iframe);

    this.iframeQueue.push(iframe.id);
  }
}
